import { c } from '../../utils/tailwind-merge';
import { RichText } from '../RichText/RichText';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableRow,
} from '../Table/Table';
import { ProductCompatibilityTableProps } from './types';

const ProductCompatibilitySinglePackage = ({
  singlePackageData,
}: {
  singlePackageData: ProductCompatibilityTableProps['singlePackageData'];
}) => {
  const {
    unityVersion,
    renderPipelines,
    buildTarget,
    fileSize,
    latestVersion,
    latestReleaseDate,
    microcopy,
  } = singlePackageData ?? {};

  const formatValue = (value: string | string[]) =>
    Array.isArray(value) ? value.join(', ') : value;

  const rows = [
    [microcopy?.unityVersion, unityVersion],
    [microcopy?.renderPipelines, renderPipelines],
    [microcopy?.buildTarget, buildTarget],
    [microcopy?.fileSize, fileSize],
    [microcopy?.latestVersion, latestVersion],
    [microcopy?.latestRelease, latestReleaseDate],
  ].filter(([label, value]) => label && value) as Array<
    [string, string | string[]]
  >;

  const LABEL_LENGTH_BREAK_THRESHOLD = 10;

  return (
    <Table>
      <TableCaption>{microcopy?.title}</TableCaption>
      <TableBody>
        {rows.map(([label, value]) => (
          <TableRow key={label}>
            <TableCell>{label}</TableCell>
            <TableCell
              className={c(
                label.length > LABEL_LENGTH_BREAK_THRESHOLD
                  ? 'break-normal'
                  : 'break-all'
              )}
            >
              {formatValue(value)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const ProductCompatibilityMultiPackages = ({
  multiPackageData,
}: {
  multiPackageData: ProductCompatibilityTableProps['multiPackageData'];
}) => {
  const { className = '', microcopy } = multiPackageData ?? {};

  return (
    <div className="relative w-full max-w-[385px]">
      <p
        className={c(
          'body-small-medium text-muted-foreground mb-4 break-all text-left',
          className
        )}
      >
        {microcopy?.title}
      </p>
      <RichText
        className={c(
          'tiny-text',
          '[&_a]:tiny-text-link [&_a]:text-blue-hover [&_a]:no-underline ',
          '[&_a:visited]:tiny-text-link',
          className
        )}
        html={microcopy ? microcopy.description : ''}
      />
    </div>
  );
};

export const ProductCompatibilityTable = (
  props: ProductCompatibilityTableProps
) => {
  if (props.variant === 'single-package') {
    return (
      <ProductCompatibilitySinglePackage
        singlePackageData={props.singlePackageData}
      />
    );
  }

  return (
    <ProductCompatibilityMultiPackages
      multiPackageData={props.multiPackageData}
    />
  );
};
